import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { deleteUsuario, listRoles, listUsuarios, type Usuario } from "@/api/usuarios";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { DataTable } from "@/components/ui/DataTable";
import { Input } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { useConfirmDelete } from "@/components/ui/useConfirmDelete";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { UsuarioRow } from "@/components/features/usuarios/UsuarioRow";
import { useAuth } from "@/contexts/AuthContext";

const USUARIOS_HEADERS = [
  "Nombre",
  "Email",
  "Rol",
  "Estado",
  "Acciones",
] as const;
const PAGE_SIZE = 50;

export const UsuariosPage = () => {
  const { user: currentUser } = useAuth();
  const [page, setPage] = useState(1);
  const { value: search, setValue: setSearch, debounced: debouncedSearch } =
    useDebouncedSearch("", 400);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["usuarios", page, debouncedSearch],
    queryFn: () => listUsuarios({ page, limit: PAGE_SIZE, q: debouncedSearch }),
  });

  const usuarios = data?.data ?? [];
  const pagination = data?.pagination;

  useQuery({
    queryKey: ["roles"],
    queryFn: listRoles,
    staleTime: 60_000,
  });

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { open, close, description, isLoading: deleting, confirm, pending } =
    useConfirmDelete<Usuario>({
      queryKey: ["usuarios", page, debouncedSearch],
      mutationFn: (u) => deleteUsuario(u.id),
      entityLabel: "Usuario",
      successMessage: "Usuario eliminado",
      formatDescription: (u) =>
        `Vas a eliminar a "${u.nombre}" (${u.email}). Esta acción no se puede deshacer.`,
      onSuccess: () => {
        void refetch();
      },
    });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Gestión de usuarios"
        subtitle={`${pagination?.total ?? usuarios.length} usuario(s) en el sistema`}
        actions={
          <Link to="/usuarios/nuevo">
            <Button>Crear usuario</Button>
          </Link>
        }
      />

      <Input
        placeholder="Buscar por nombre o email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-md"
      />

      <DataTable<Usuario>
        headers={USUARIOS_HEADERS}
        data={usuarios}
        isLoading={isLoading || isFetching}
        emptyMessage="No se encontraron usuarios."
        rowKey={(u) => u.id}
        renderRow={(u) => (
          <UsuarioRow
            usuario={u}
            currentUserId={currentUser?.id}
            onDelete={open}
          />
        )}
      />

      {pagination ? (
        <PaginationBar
          page={page}
          totalPages={pagination.total_pages}
          onPageChange={setPage}
          itemCount={usuarios.length}
          totalCount={pagination.total}
          itemLabel="usuarios"
        />
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title="¿Eliminar usuario?"
        description={description}
        confirmLabel="Sí, eliminar"
        cancelLabel="Cancelar"
        destructive
        loading={deleting}
        onConfirm={confirm}
        onCancel={close}
      />
    </div>
  );
};